import imageSegmentationApi from '@frontend/services/api/image-segmentation';
import { imageSegmentationEventBroker, IMAGE_SEGMENTATION_EVENTS } from './event-broker';

/**
 * Walks several images through the segmentation preview one after another so they land in a
 * single layout, one template each.
 *
 * Nothing is written until the last image is confirmed. The choices for every image are held
 * here and the layout is built in one go at the end, which is what makes closing the modal
 * half way through leave nothing behind: there is no half-built layout to delete, because
 * there is no layout yet.
 *
 * The analyses run a little ahead of the user rather than all at once. An analysis is not
 * queued work: it runs inside the PHP worker that took the request, for a minute or so, so
 * starting a dozen of them takes every worker the site has and leaves none to answer the
 * page the user is looking at — including the polling that tells them how their first image
 * is getting on. A window of two keeps one image warming while another is reviewed, which is
 * all the waiting there is to hide, and leaves the site responsive.
 */
/**
 * How far ahead of the image on screen to start analysing. One means two at a time: the one
 * being reviewed and the next. The user cannot move past an image until its analysis is done,
 * so this is a ceiling on how many can ever be running, not just on how many are started.
 */
const ANALYSIS_LOOKAHEAD = 1;

const state = {
    active: false,
    images: [],
    pending: [],
    index: 0,
    customerId: 0,
    targetLayoutId: null,
    layoutName: null,
    choices: [],
    skipped: 0,
    abandoned: 0,
};

function reset() {
    state.active = false;
    state.images = [];
    state.pending = [];
    state.index = 0;
    state.customerId = 0;
    state.targetLayoutId = null;
    state.layoutName = null;
    state.choices = [];
    state.skipped = 0;
    state.abandoned = 0;
}

function imagePathOf(image) {
    return image.original_path || image.path || '';
}

/**
 * Start the analysis for the given image and the ones just ahead of it, if they have not
 * been started already. Cheap to call repeatedly: a slot that is filled is left alone.
 *
 * @param {number} from Index of the image on screen
 */
function startAnalysesAround(from) {
    const last = Math.min(from + ANALYSIS_LOOKAHEAD, state.images.length - 1);

    for (let index = from; index <= last; index++) {
        if (state.pending[index]) {
            continue;
        }

        state.pending[index] = imageSegmentationApi
            .startSegmentation(state.customerId, imagePathOf(state.images[index]), null, state.layoutName)
            .then(response => response.data);
    }
}

/** Show the preview for the image at $index, skipping any whose job never started. */
async function showCurrent() {
    while (state.active && state.index < state.pending.length) {
        startAnalysesAround(state.index);

        const image = state.images[state.index];
        const position = state.index + 1;
        const common = {
            customerId: state.customerId,
            layoutId: null, // the layout is built at the end, never during the walk
            queuePosition: position,
            queueTotal: state.pending.length,
            queueImageName: image.name || '',
        };

        let data;
        try {
            data = await state.pending[state.index];
        } catch (error) {
            console.error('Segmentation failed to start for an image in the queue:', error);
            data = null;
        }

        if (!state.active) {
            return;
        }

        if (!data || data.success === false || !data.job_uuid) {
            toastr.warning(`Skipped "${image.name || 'image'}" — it could not be processed.`);
            state.skipped++;
            state.index++;
            continue;
        }

        if (data.cached && data.layer_data) {
            imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_CACHED_PREVIEW, {
                ...common,
                jobUuid: data.job_uuid,
                imagePath: imagePathOf(image),
                originalImagePath: data.original_image_path,
                stale: data.stale === true,
                displayedWidth: data.displayed_width,
                displayedHeight: data.displayed_height,
                cachedData: {
                    layers: data.layer_data,
                    inpaintedBackgroundUrl: data.inpainted_background_path || '',
                },
            });
        } else {
            imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.SHOW_PREVIEW, {
                ...common,
                jobUuid: data.job_uuid,
                imagePath: imagePathOf(image),
                originalImageUrl: imagePathOf(image),
                layoutName: state.layoutName || '',
            });
        }

        return;
    }

    if (state.active) {
        await finish();
    }
}

/** Build the layout from everything collected: the first call creates it, the rest add to it. */
async function finish() {
    const choices = state.choices;

    if (choices.length === 0) {
        toastr.warning('None of the images could be processed, so no layout was created.');
        reset();
        imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.QUEUE_FAILED, {});
        return;
    }

    let layoutId = state.targetLayoutId;
    let customerId = state.customerId;
    let redirectUrl = null;
    let failed = 0;

    for (const choice of choices) {
        try {
            const { data } = await imageSegmentationApi.createFromSegmentation(
                state.customerId || 0,
                choice.jobUuid,
                choice.selectedLayerIds,
                layoutId ? null : state.layoutName,
                layoutId,
                choice.convertToTextLayerIds,
                choice.extractionMode,
                choice.removeBgLayerIds,
                choice.ovalShadowLayerIds
            );

            if (!data || !data.success) {
                throw new Error(data && data.message ? data.message : 'Failed to add the image to the layout');
            }

            layoutId = data.layout_id;
            customerId = data.customer_id || customerId;
            redirectUrl = data.redirect_url || redirectUrl;
        } catch (error) {
            console.error('Failed to add a queued image to the layout:', error);
            failed++;
        }
    }

    const total = choices.length;
    const skipped = state.skipped;
    const abandoned = state.abandoned;
    reset();

    if (!layoutId) {
        toastr.error('Could not create the layout from the selected images.');
        imageSegmentationEventBroker.fire(IMAGE_SEGMENTATION_EVENTS.QUEUE_FAILED, {});
        return;
    }

    const added = total - failed;
    const notAdded = failed + skipped;
    if (notAdded > 0) {
        toastr.warning(`${added} of ${added + notAdded} images were added. The rest could not be processed.`);
    } else if (abandoned > 0) {
        // Said plainly, because leaving the rest out was the user's own doing, not a failure.
        toastr.success(
            `Layout created with ${added} image${added !== 1 ? 's' : ''}. `
            + `${abandoned} remaining image${abandoned !== 1 ? 's were' : ' was'} skipped.`
        );
    } else {
        toastr.success(`Layout created with ${added} image${added !== 1 ? 's' : ''}!`);
    }

    window.location.href = redirectUrl || `/banner/${customerId}/group/${layoutId}/show`;
}

export const segmentationQueue = {
    isActive() {
        return state.active;
    },

    progress() {
        return {
            position: state.index + 1,
            total: state.pending.length,
        };
    },

    /**
     * Begin walking a set of images through the preview.
     *
     * @param {object} options
     * @param {object[]} options.images File browser items to segment
     * @param {number} options.customerId Customer the layout belongs to
     * @param {number|null} options.targetLayoutId Existing layout to add to, or null for a new one
     * @param {string|null} options.layoutName Name for the new layout
     */
    start({ images, customerId, targetLayoutId = null, layoutName = null }) {
        reset();
        state.active = true;
        state.images = images;
        state.customerId = customerId;
        state.targetLayoutId = targetLayoutId;
        state.layoutName = layoutName;

        // One slot per image, filled as the walk reaches them. Sized up front so everything
        // that reports progress can count on it.
        state.pending = new Array(images.length).fill(null);

        return showCurrent();
    },

    /**
     * Record what the user chose for the image on screen and move on.
     *
     * @param {object} choice Layer selection and per-image options
     * @returns {Promise} Resolves once the next image is on screen, or the layout is built
     */
    collect(choice) {
        if (!state.active) {
            return Promise.resolve();
        }

        state.choices.push(choice);
        state.index++;

        return showCurrent();
    },

    /**
     * Take the image on screen and build the layout now, leaving the rest of the queue out.
     *
     * The analyses already under way cannot be called back, but nothing waits on them and
     * nothing they produce is used: their jobs finish on the server and their results sit in
     * the cache, ready if the same images are imported again.
     *
     * @param {object} choice Layer selection and per-image options for the image on screen
     * @returns {Promise} Resolves once the layout has been built
     */
    finishHere(choice) {
        if (!state.active) {
            return Promise.resolve();
        }

        state.choices.push(choice);
        state.index++;
        state.abandoned = Math.max(0, state.pending.length - state.index);

        return finish();
    },

    /** Drop the queue. Nothing has been written yet, so there is nothing to undo. */
    cancel() {
        if (!state.active) {
            return;
        }

        const remaining = state.pending.length - state.index;
        reset();

        if (remaining > 0) {
            toastr.info('Import cancelled. No layout was created.');
        }
    },
};

imageSegmentationEventBroker.on(IMAGE_SEGMENTATION_EVENTS.CLOSED, () => {
    segmentationQueue.cancel();
});

imageSegmentationEventBroker.on(IMAGE_SEGMENTATION_EVENTS.JOB_FAILED, () => {
    if (!segmentationQueue.isActive()) {
        return;
    }

    // "Skip it and carry on" — the modal has already told the user this one failed.
    state.skipped++;
    state.index++;
    showCurrent();
});

export default segmentationQueue;
